/**
 * Registry of every extension the detector knows about — in two tiers.
 *
 *  - Curated modules: one hand-written `<slug>.ts` per extension, imported and listed
 *    below. This is where behavioural detectors, edge cases, and anything needing bespoke
 *    logic or explanation live. To add one: create the module, then register it here.
 *  - Generated records: `generated-records.json`, compact resource-probe detectors
 *    produced by `tooling/generate-detectors.js`. This is how the set scales to thousands
 *    without a module each.
 *
 * The two are composed at the bottom; a curated module always overrides a generated record
 * of the same id (so you can promote a record to a hand-written detector by adding a module).
 */

import type { DetectorRecord, ExtensionDefinition } from '../core/types';
import { fromRecord } from '../core/from-record';
import generatedRecords from './generated-records.json';

import { adobeAcrobat } from './adobe-acrobat';
import { grammarly } from './grammarly';
import { googleTranslate } from './google-translate';
import { internetProtection360 } from './360-internet-protection';
import { veepn } from './veepn';
import { malwarebytes } from './malwarebytes';
import { onlineSecurity } from './online-security';
import { capitalOneShopping } from './capital-one-shopping';
import { teleparty } from './teleparty';
import { nordpass } from './nordpass';
import { aviraBrowserSafety } from './avira-browser-safety';
import { wpsPdf } from './wps-pdf';
import { zoom } from './zoom';
import { loom } from './loom';
import { anydocTranslator } from './anydoc-translator';
import { darkReader } from './dark-reader';
import { googleKeep } from './google-keep';
import { endnoteClick } from './endnote-click';
import { colorzilla } from './colorzilla';
import { deepl } from './deepl';
import { btroblox } from './btroblox';
import { ieTab } from './ie-tab';
import { screencastify } from './screencastify';
import { keepa } from './keepa';
import { duckduckgo } from './duckduckgo';
import { rakuten } from './rakuten';
import { kami } from './kami';
import { readAndWrite } from './read-and-write';
import { tagAssistant } from './tag-assistant';
import { standsAdblocker } from './stands-adblocker';
import { googleInputTools } from './google-input-tools';
import { coupert } from './coupert';
import { mailtrack } from './mailtrack';
import { awesomeScreenshot } from './awesome-screenshot';
import { monica } from './monica';
import { vidiq } from './vidiq';
import { ropro } from './ropro';
import { sevenTv } from './seven-tv';
import { keeper } from './keeper';
import { poperBlocker } from './poper-blocker';
import { imageDownloader } from './image-downloader';
import { stylish } from './stylish';
import { languageReactor } from './language-reactor';
import { uboostVpn } from './uboost-vpn';
import { pieAdblock } from './pie-adblock';
import { googleDictionary } from './google-dictionary';
import { smallpdf } from './smallpdf';
import { languagetool } from './languagetool';
import { seoquake } from './seoquake';
import { apolloIo } from './apollo-io';
import { turnOffTheLights } from './turn-off-the-lights';
import { bitmoji } from './bitmoji';
import { trafficlight } from './trafficlight';
import { mobileSimulator } from './mobile-simulator';
import { stylus } from './stylus';
import { steamInventoryHelper } from './steam-inventory-helper';
import { multipassword } from './multipassword';
import { darkModeExt } from './dark-mode';
import { blocksite } from './blocksite';
import { screenshotReader } from './screenshot-reader';
import { scribbr } from './scribbr';
import { frankerfacez } from './frankerfacez';
import { dashlane } from './dashlane';
import { clickAndClean } from './click-and-clean';
import { shimeji } from './shimeji';
import { keywordsEverywhere } from './keywords-everywhere';
import { autoRefreshPlus } from './auto-refresh-plus';
import { equatio } from './equatio';
import { eyeDropper } from './eye-dropper';
import { similarweb } from './similarweb';
import { scrnliRecorder } from './scrnli-recorder';
import { transpose } from './transpose';
import { csfloat } from './csfloat';
import { reddiEnhancementSuite } from './reddit-enhancement-suite';

// --- ranks 101-200 ---
import { scener } from './scener';
import { libkeyNomad } from './libkey-nomad';
import { pdfViewer } from './pdf-viewer';
import { instantDataScraper } from './instant-data-scraper';
import { printfriendly } from './printfriendly';
import { textBlaze } from './text-blaze';
import { letyshops } from './letyshops';
import { watch2gether } from './watch2gether';
import { speechify } from './speechify';
import { colorPicker } from './color-picker';
import { chatbotsPlace } from './chatbotsplace';
import { translateForChrome } from './translate-for-chrome';
import { fontFinder } from './font-finder';
import { angularDevtools } from './angular-devtools';
import { unhook } from './unhook';
import { gnomeShellIntegration } from './gnome-shell-integration';
import { wordtune } from './wordtune';
import { obsidianWebClipper } from './obsidian-web-clipper';
import { googleMeetEnhancedExperience } from './google-meet-enhanced-experience';
import { copyfish } from './copyfish';
import { turboDownloaderForInstagram } from './turbo-downloader-for-instagram';
import { wanteeed } from './wanteeed';
import { alerabat } from './alerabat';
import { responsiveViewer } from './responsive-viewer';
import { aiprmForChatgpt } from './aiprm-for-chatgpt';
import { steamdb } from './steamdb';
import { youtubeSummaryChatgptClaude } from './youtube-summary-chatgpt-claude';
import { mozBar } from './mozbar';
import { tasksForCanvas } from './tasks-for-canvas';
import { adBlockAdblocker } from './ad-block-adblocker';
import { guardioProtection } from './guardio-protection';
import { beyond20 } from './beyond-20';
import { karma } from './karma';
import { gptzero } from './gptzero';
import { enhancerForYoutube } from './enhancer-for-youtube';
import { virtruEmailProtection } from './virtru-email-protection';
import { briskTeaching } from './brisk-teaching';
import { fontsNinja } from './fonts-ninja';
import { colorpickEyedropper } from './colorpick-eyedropper';
import { windowResizer } from './window-resizer';
import { detailedSeo } from './detailed-seo';
import { rssFeedReader } from './rss-feed-reader';
import { adBlockWonder } from './ad-block-wonder';
import { igraal } from './igraal';
import { checkerPlusForGmail } from './checker-plus-for-gmail';
import { oceanHero } from './oceanhero';
import { myBib } from './mybib';
import { orbitNote } from './orbitnote';
import { allowCopy } from './allow-copy';
import { roGold } from './rogold';
import { voiceIn } from './voice-in';
import { preMid } from './premid';
import { fantasyPros } from './fantasypros';
import { axeDevtools } from './axe-devtools';
import { magicActions } from './magic-actions';
import { augmentedSteam } from './augmented-steam';
import { aitopia } from './aitopia';
import { unpaywall } from './unpaywall';
import { ejoyAiDictionary } from './ejoy-ai-dictionary';
import { fetchv } from './fetchv';
import { equalizer } from './equalizer';
import { mote } from './mote';
import { muzli } from './muzli';
import { speedDialFvd } from './speed-dial-fvd';
import { glean } from './glean';
import { hubspotSales } from './hubspot-sales';
import { buyhatke } from './buyhatke';
import { alitools } from './alitools';
import { webScraper } from './web-scraper';
import { salesforceInspector } from './salesforce-inspector';
import { smarty } from './smarty';
import { sweezyCursors } from './sweezy-cursors';
import { cssPeeper } from './css-peeper';
// --- ranks 201-300 ---
import { citeThisForMe } from './cite-this-for-me';
import { composeAi } from './compose-ai';
import { enableCopyPaste } from './enable-copy-paste';
import { trancy } from './trancy';
import { tripchipper } from './tripchipper';
import { mouseTooltipTranslator } from './mouse-tooltip-translator';
import { customProgressBar } from './custom-progress-bar';
import { helperbird } from './helperbird';
import { cursorStyle } from './cursor-style';
import { rememberry } from './rememberry';
import { readAloud } from './read-aloud';
import { perfectPixel } from './perfect-pixel';
import { swagbutton } from './swagbutton';
import { pieShopping } from './pie-shopping';
import { justRead } from './just-read';
import { jam } from './jam';
import { downloadAllImages } from './download-all-images';
import { flickcall } from './flickcall';
import { opendyslexic } from './opendyslexic';
import { gmailReverseConversation } from './gmail-reverse-conversation';
import { urbanBrowserGuard } from './urban-browser-guard';
import { readerView } from './reader-view';
import { magical } from './magical';
import { flashPlayerEnable } from './flash-player-enable';
import { topcashback } from './topcashback';
import { qantasShopping } from './qantas-shopping';
import { edpuzzle } from './edpuzzle';
import { translatorDictionary } from './translator-dictionary';
import { seamlessAi } from './seamless-ai';
import { roseal } from './roseal';
import { darkModeNightEye } from './dark-mode-night-eye';
import { jsonViewerPro } from './json-viewer-pro';
import { addToBabylistButton } from './add-to-babylist-button';
import { hoverZoom } from './hover-zoom';
import { webrtcControl } from './webrtc-control';
import { joko } from './joko';
import { reverso } from './reverso';
import { viitorTranslate } from './viitor-translate';
import { languageLearningNetflixYoutube } from './language-learning-netflix-youtube';
import { waIncognito } from './waincognito';
import { cursorHelper } from './cursor-helper';
import { catGatekeeper } from './cat-gatekeeper';
import { substital } from './substital';
import { googleArtsAndCulture } from './google-arts-and-culture';
import { linguist } from './linguist';
import { rikaikun } from './rikaikun';
import { volumeControl } from './volume-control';
import { foeHelper } from './foe-helper';
import { insertlearning } from './insertlearning';
import { imageDownloaderSavePictures } from './image-downloader-save-pictures';
import { mino } from './mino';
import { requestly } from './requestly';
import { hypothesis } from './hypothesis';
import { webScrobbler } from './web-scrobbler';
import { toucan } from './toucan';
import { customCalendarBackground } from './custom-calendar-background';
import { truffle } from './truffle';
import { tabReloader } from './tab-reloader';
import { purchaseAssistant1688 } from './purchase-assistant-1688';
import { roboform } from './roboform';
import { knowt } from './knowt';
import { vencordWeb } from './vencord-web';
import { youtubeShortsBlock } from './youtube-shorts-block';
import { netflixPip } from './netflix-pip';
import { ffzAddonPack } from './ffz-addon-pack';
import { privacyExtensionWhatsapp } from './privacy-extension-whatsapp';
import { bloxfinder } from './bloxfinder';
// --- ranks 301-400 ---
import { adBlockNinja } from "./ad-block-ninja";
import { alibabaLens } from "./alibaba-lens";
import { aliHelper } from "./alihelper";
import { alipriceSourcingTool } from "./aliprice-sourcing-tool";
import { aliSavePlus } from "./alisave-plus";
import { aura } from "./aura";
import { autoBccForGmail } from "./auto-bcc-for-gmail";
import { beFrugal } from "./befrugal";
import { betterLyrics } from "./better-lyrics";
import { betterPathofexileTrading } from "./better-pathofexile-trading";
import { betterblox } from "./betterblox";
import { cently } from "./cently";
import { cheapFlightFinder } from "./cheap-flight-finder";
import { cleaner } from "./cleaner";
import { clickToRemoveElement } from "./click-to-remove-element";
import { cloudhqEmailTemplates } from "./cloudhq-email-templates";
import { clownfishVoiceChanger } from "./clownfish-voice-changer";
import { cursorTrails } from "./cursor-trails";
import { dearrow } from "./dearrow";
import { docsAfterDark } from "./docsafterdark";
import { enhancer } from "./enhancer";
import { enpass } from "./enpass";
import { exifViewerClassic } from "./exif-viewer-classic";
import { faceitForecast } from "./faceit-forecast";
import { fastsave } from "./fastsave";
import { flowCrypt } from "./flowcrypt";
import { gmailApp } from "./gmail-app";
import { happyDog } from "./happy-dog";
import { hyperwrite } from "./hyperwrite";
import { influencerAnalytics } from "./influencer-analytics";
import { instaText } from "./instatext";
import { invidWeverify } from "./invid-weverify";
import { kolsprite } from "./kolsprite";
import { liveStartPage } from "./live-start-page";
import { mailtracker } from "./mailtracker";
import { mailvelope } from "./mailvelope";
import { malSync } from "./mal-sync";
import { meowTheCatPet } from "./meow-the-cat-pet";
import { meowsing } from "./meowsing";
import { merciApp } from "./merciapp";
import { oldTwitterLayout } from "./old-twitter-layout";
import { onlineVideoDownloader } from "./online-video-downloader";
import { openIncognitoTab } from "./open-incognito-tab";
import { pocketUniverse } from "./pocket-universe";
import { pointsPath } from "./points-path";
import { readerMode } from "./reader-mode";
import { revisionHistory } from "./revision-history";
import { saveToCosmos } from "./save-to-cosmos";
import { screenpal } from "./screenpal";
import { sortFeed } from "./sort-feed";
import { sweezyProgressBar } from "./sweezy-progress-bar";
import { tabSuspender } from "./tab-suspender";
import { tileTabsWe } from "./tile-tabs-we";
import { untrapForYouTube } from "./untrap-for-youtube";
import { vimiumC } from "./vimium-c";
import { voila } from "./voila";
import { youtubeLivechatFlusher } from "./youtube-livechat-flusher";
import { youtubeTvOnPc } from "./youtube-tv-on-pc";
import { zight } from "./zight";

// --- ranks 301-400: behavioural + glob-WAR detectors ---
import { fasterweb } from "./fasterweb";
import { giphyForGmail } from "./giphy-for-gmail";
import { highlightThis } from "./highlight-this";
import { saveStoryInstagram } from "./save-story-instagram";
import { screenShader } from "./screen-shader";
import { skipAdsYoutube } from "./skip-ads-youtube";

// --- ranks 401-500 ---
import { adSkipperForYoutube } from "./ad-skipper-for-youtube";
import { advancedAudioEqualizer } from "./advanced-audio-equalizer";
import { appblock } from "./appblock";
import { audioEqualizer } from "./audio-equalizer";
import { battleTabs } from "./battletabs";
import { bestCursors } from "./best-cursors";
import { breathe } from "./breathe";
import { broMon } from "./bro-mon";
import { cheaperthere } from "./cheaperthere";
import { crankwheel } from "./crankwheel";
import { cursorful } from "./cursorful";
import { enhancedImageViewer } from "./enhanced-image-viewer";
import { feedlyNotifier } from "./feedly-notifier";
import { fontstorage } from "./fontstorage";
import { goodMorningCloudhq } from "./good-morning-cloudhq";
import { googleMapsExtendedRoutes } from "./google-maps-extended-routes";
import { grabit } from "./grabit";
import { groundNews } from "./ground-news";
import { htmlEditorForGmail } from "./html-editor-for-gmail";
import { imgurUploader } from "./imgur-uploader";
import { inoreader } from "./inoreader";
import { intouchapp } from "./intouchapp";
import { kommodo } from "./kommodo";
import { mailMergeMailmeteor } from "./mail-merge-mailmeteor";
import { mouseCursor } from "./mouse-cursor";
import { ntlModSlitherIo } from "./ntl-mod-slither-io";
import { postureMinder } from "./posture-minder";
import { previews } from "./previews";
import { promptCard } from "./promptcard";
import { ratepunk } from "./ratepunk";
import { remindersForGoogleCalendar } from "./reminders-for-google-calendar";
import { removeBackground } from "./remove-background";
import { salesSortEbay } from "./sales-sort-ebay";
import { sauceForStrava } from "./sauce-for-strava";
import { socialFocus } from "./socialfocus";
import { stormTracker } from "./storm-tracker";
import { streamingEnhanced } from "./streaming-enhanced";
import { syncWatch } from "./sync-watch";
import { synq } from "./synq";
import { testingToolkit } from "./testing-toolkit";
import { theRssAggregator } from "./the-rss-aggregator";
import { themesongYoutubeMusic } from "./themesong-youtube-music";
import { tripadvisor } from "./tripadvisor";
import { tzofar } from "./tzofar";
import { ultimate432HzConverter } from "./ultimate-432hz-converter";
import { ultrawideStreaming } from "./ultrawide-streaming";
import { videoMaximizer } from "./video-maximizer";
import { vkNext } from "./vk-next";
import { wordReplacerMax } from "./word-replacer-max";

const curated: ExtensionDefinition[] = [
  adobeAcrobat,
  grammarly,
  googleTranslate,
  internetProtection360,
  veepn,
  malwarebytes,
  onlineSecurity,
  capitalOneShopping,
  teleparty,
  nordpass,
  aviraBrowserSafety,
  wpsPdf,
  zoom,
  loom,
  anydocTranslator,
  darkReader,
  googleKeep,
  endnoteClick,
  colorzilla,
  deepl,
  btroblox,
  ieTab,
  screencastify,
  keepa,
  duckduckgo,
  rakuten,
  kami,
  readAndWrite,
  tagAssistant,
  standsAdblocker,
  googleInputTools,
  coupert,
  mailtrack,
  awesomeScreenshot,
  monica,
  vidiq,
  ropro,
  sevenTv,
  keeper,
  poperBlocker,
  imageDownloader,
  stylish,
  languageReactor,
  uboostVpn,
  pieAdblock,
  googleDictionary,
  smallpdf,
  languagetool,
  seoquake,
  apolloIo,
  turnOffTheLights,
  bitmoji,
  trafficlight,
  mobileSimulator,
  stylus,
  steamInventoryHelper,
  multipassword,
  darkModeExt,
  blocksite,
  screenshotReader,
  scribbr,
  frankerfacez,
  dashlane,
  clickAndClean,
  shimeji,
  keywordsEverywhere,
  autoRefreshPlus,
  equatio,
  eyeDropper,
  similarweb,
  scrnliRecorder,
  transpose,
  csfloat,
  reddiEnhancementSuite,
  scener,
  libkeyNomad,
  pdfViewer,
  instantDataScraper,
  printfriendly,
  textBlaze,
  letyshops,
  watch2gether,
  speechify,
  colorPicker,
  chatbotsPlace,
  translateForChrome,
  fontFinder,
  angularDevtools,
  unhook,
  gnomeShellIntegration,
  wordtune,
  obsidianWebClipper,
  googleMeetEnhancedExperience,
  copyfish,
  turboDownloaderForInstagram,
  wanteeed,
  alerabat,
  responsiveViewer,
  aiprmForChatgpt,
  steamdb,
  youtubeSummaryChatgptClaude,
  mozBar,
  tasksForCanvas,
  adBlockAdblocker,
  guardioProtection,
  beyond20,
  karma,
  gptzero,
  enhancerForYoutube,
  virtruEmailProtection,
  briskTeaching,
  fontsNinja,
  colorpickEyedropper,
  windowResizer,
  detailedSeo,
  rssFeedReader,
  adBlockWonder,
  igraal,
  checkerPlusForGmail,
  oceanHero,
  myBib,
  orbitNote,
  allowCopy,
  roGold,
  voiceIn,
  preMid,
  fantasyPros,
  axeDevtools,
  magicActions,
  augmentedSteam,
  aitopia,
  unpaywall,
  ejoyAiDictionary,
  fetchv,
  equalizer,
  mote,
  muzli,
  speedDialFvd,
  glean,
  hubspotSales,
  buyhatke,
  alitools,
  webScraper,
  salesforceInspector,
  smarty,
  sweezyCursors,
  cssPeeper,
  citeThisForMe,
  composeAi,
  enableCopyPaste,
  trancy,
  tripchipper,
  mouseTooltipTranslator,
  customProgressBar,
  helperbird,
  cursorStyle,
  rememberry,
  readAloud,
  perfectPixel,
  swagbutton,
  pieShopping,
  justRead,
  jam,
  downloadAllImages,
  flickcall,
  opendyslexic,
  gmailReverseConversation,
  urbanBrowserGuard,
  readerView,
  magical,
  flashPlayerEnable,
  topcashback,
  qantasShopping,
  edpuzzle,
  translatorDictionary,
  seamlessAi,
  roseal,
  darkModeNightEye,
  jsonViewerPro,
  addToBabylistButton,
  hoverZoom,
  webrtcControl,
  joko,
  reverso,
  viitorTranslate,
  languageLearningNetflixYoutube,
  waIncognito,
  cursorHelper,
  catGatekeeper,
  substital,
  googleArtsAndCulture,
  linguist,
  rikaikun,
  volumeControl,
  foeHelper,
  insertlearning,
  imageDownloaderSavePictures,
  mino,
  requestly,
  hypothesis,
  webScrobbler,
  toucan,
  customCalendarBackground,
  truffle,
  tabReloader,
  knowt,
  vencordWeb,
  youtubeShortsBlock,
  netflixPip,
  ffzAddonPack,
  privacyExtensionWhatsapp,
  bloxfinder,
  purchaseAssistant1688,
  roboform,
  adBlockNinja,
  alibabaLens,
  aliHelper,
  alipriceSourcingTool,
  aliSavePlus,
  aura,
  autoBccForGmail,
  beFrugal,
  betterLyrics,
  betterPathofexileTrading,
  betterblox,
  cently,
  cheapFlightFinder,
  cleaner,
  clickToRemoveElement,
  cloudhqEmailTemplates,
  clownfishVoiceChanger,
  cursorTrails,
  dearrow,
  docsAfterDark,
  enhancer,
  enpass,
  exifViewerClassic,
  faceitForecast,
  fastsave,
  flowCrypt,
  gmailApp,
  happyDog,
  hyperwrite,
  influencerAnalytics,
  instaText,
  invidWeverify,
  kolsprite,
  liveStartPage,
  mailtracker,
  mailvelope,
  malSync,
  meowTheCatPet,
  meowsing,
  merciApp,
  oldTwitterLayout,
  onlineVideoDownloader,
  openIncognitoTab,
  pocketUniverse,
  pointsPath,
  readerMode,
  revisionHistory,
  saveToCosmos,
  screenpal,
  sortFeed,
  sweezyProgressBar,
  tabSuspender,
  tileTabsWe,
  untrapForYouTube,
  vimiumC,
  voila,
  youtubeLivechatFlusher,
  youtubeTvOnPc,
  zight,
  fasterweb,
  giphyForGmail,
  highlightThis,
  saveStoryInstagram,
  screenShader,
  skipAdsYoutube,
  adSkipperForYoutube,
  advancedAudioEqualizer,
  appblock,
  audioEqualizer,
  battleTabs,
  bestCursors,
  breathe,
  broMon,
  cheaperthere,
  crankwheel,
  cursorful,
  enhancedImageViewer,
  feedlyNotifier,
  fontstorage,
  goodMorningCloudhq,
  googleMapsExtendedRoutes,
  grabit,
  groundNews,
  htmlEditorForGmail,
  imgurUploader,
  inoreader,
  intouchapp,
  kommodo,
  mailMergeMailmeteor,
  mouseCursor,
  ntlModSlitherIo,
  postureMinder,
  previews,
  promptCard,
  ratepunk,
  remindersForGoogleCalendar,
  removeBackground,
  salesSortEbay,
  sauceForStrava,
  socialFocus,
  stormTracker,
  streamingEnhanced,
  syncWatch,
  synq,
  testingToolkit,
  theRssAggregator,
  themesongYoutubeMusic,
  tripadvisor,
  tzofar,
  ultimate432HzConverter,
  ultrawideStreaming,
  videoMaximizer,
  vkNext,
  wordReplacerMax,
];

// Generated resource-probe detectors, minus any id already covered by a curated module.
const curatedIds = new Set(curated.map((e) => e.id));
const generated: ExtensionDefinition[] = (generatedRecords as DetectorRecord[])
  .filter((r) => !curatedIds.has(r.id))
  .map(fromRecord);

/** Every extension the detector knows about: curated modules first, then generated records. */
export const extensions: ExtensionDefinition[] = [...curated, ...generated];

export const byId = new Map(extensions.map((e) => [e.id, e]));

export {
  adobeAcrobat,
  grammarly,
  googleTranslate,
  internetProtection360,
  veepn,
  malwarebytes,
  onlineSecurity,
  capitalOneShopping,
  teleparty,
  nordpass,
  aviraBrowserSafety,
  wpsPdf,
  zoom,
  loom,
  anydocTranslator,
  darkReader,
  googleKeep,
  endnoteClick,
  colorzilla,
  deepl,
  btroblox,
  ieTab,
  screencastify,
  keepa,
  duckduckgo,
  rakuten,
  kami,
  readAndWrite,
  tagAssistant,
  standsAdblocker,
  googleInputTools,
  coupert,
  mailtrack,
  awesomeScreenshot,
  monica,
  vidiq,
  ropro,
  sevenTv,
  keeper,
  poperBlocker,
  imageDownloader,
  stylish,
  languageReactor,
  uboostVpn,
  pieAdblock,
  googleDictionary,
  smallpdf,
  languagetool,
  seoquake,
  apolloIo,
  turnOffTheLights,
  bitmoji,
  trafficlight,
  mobileSimulator,
  stylus,
  steamInventoryHelper,
  multipassword,
  darkModeExt,
  blocksite,
  screenshotReader,
  scribbr,
  frankerfacez,
  dashlane,
  clickAndClean,
  shimeji,
  keywordsEverywhere,
  autoRefreshPlus,
  equatio,
  eyeDropper,
  similarweb,
  scrnliRecorder,
  transpose,
  csfloat,
  reddiEnhancementSuite,
  scener,
  libkeyNomad,
  pdfViewer,
  instantDataScraper,
  printfriendly,
  textBlaze,
  letyshops,
  watch2gether,
  speechify,
  colorPicker,
  chatbotsPlace,
  translateForChrome,
  fontFinder,
  angularDevtools,
  unhook,
  gnomeShellIntegration,
  wordtune,
  obsidianWebClipper,
  googleMeetEnhancedExperience,
  copyfish,
  turboDownloaderForInstagram,
  wanteeed,
  alerabat,
  responsiveViewer,
  aiprmForChatgpt,
  steamdb,
  youtubeSummaryChatgptClaude,
  mozBar,
  tasksForCanvas,
  adBlockAdblocker,
  guardioProtection,
  beyond20,
  karma,
  gptzero,
  enhancerForYoutube,
  virtruEmailProtection,
  briskTeaching,
  fontsNinja,
  colorpickEyedropper,
  windowResizer,
  detailedSeo,
  rssFeedReader,
  adBlockWonder,
  igraal,
  checkerPlusForGmail,
  oceanHero,
  myBib,
  orbitNote,
  allowCopy,
  roGold,
  voiceIn,
  preMid,
  fantasyPros,
  axeDevtools,
  magicActions,
  augmentedSteam,
  aitopia,
  unpaywall,
  ejoyAiDictionary,
  fetchv,
  equalizer,
  mote,
  muzli,
  speedDialFvd,
  glean,
  hubspotSales,
  buyhatke,
  alitools,
  webScraper,
  salesforceInspector,
  smarty,
  sweezyCursors,
  cssPeeper,
  citeThisForMe,
  composeAi,
  enableCopyPaste,
  trancy,
  tripchipper,
  mouseTooltipTranslator,
  customProgressBar,
  helperbird,
  cursorStyle,
  rememberry,
  readAloud,
  perfectPixel,
  swagbutton,
  pieShopping,
  justRead,
  jam,
  downloadAllImages,
  flickcall,
  opendyslexic,
  gmailReverseConversation,
  urbanBrowserGuard,
  readerView,
  magical,
  flashPlayerEnable,
  topcashback,
  qantasShopping,
  edpuzzle,
  translatorDictionary,
  seamlessAi,
  roseal,
  darkModeNightEye,
  jsonViewerPro,
  addToBabylistButton,
  hoverZoom,
  webrtcControl,
  joko,
  reverso,
  viitorTranslate,
  languageLearningNetflixYoutube,
  waIncognito,
  cursorHelper,
  catGatekeeper,
  substital,
  googleArtsAndCulture,
  linguist,
  rikaikun,
  volumeControl,
  foeHelper,
  insertlearning,
  imageDownloaderSavePictures,
  mino,
  requestly,
  hypothesis,
  webScrobbler,
  toucan,
  customCalendarBackground,
  truffle,
  tabReloader,
  knowt,
  vencordWeb,
  youtubeShortsBlock,
  netflixPip,
  ffzAddonPack,
  privacyExtensionWhatsapp,
  bloxfinder,
  purchaseAssistant1688,
  roboform,
  adBlockNinja,
  alibabaLens,
  aliHelper,
  alipriceSourcingTool,
  aliSavePlus,
  aura,
  autoBccForGmail,
  beFrugal,
  betterLyrics,
  betterPathofexileTrading,
  betterblox,
  cently,
  cheapFlightFinder,
  cleaner,
  clickToRemoveElement,
  cloudhqEmailTemplates,
  clownfishVoiceChanger,
  cursorTrails,
  dearrow,
  docsAfterDark,
  enhancer,
  enpass,
  exifViewerClassic,
  faceitForecast,
  fastsave,
  flowCrypt,
  gmailApp,
  happyDog,
  hyperwrite,
  influencerAnalytics,
  instaText,
  invidWeverify,
  kolsprite,
  liveStartPage,
  mailtracker,
  mailvelope,
  malSync,
  meowTheCatPet,
  meowsing,
  merciApp,
  oldTwitterLayout,
  onlineVideoDownloader,
  openIncognitoTab,
  pocketUniverse,
  pointsPath,
  readerMode,
  revisionHistory,
  saveToCosmos,
  screenpal,
  sortFeed,
  sweezyProgressBar,
  tabSuspender,
  tileTabsWe,
  untrapForYouTube,
  vimiumC,
  voila,
  youtubeLivechatFlusher,
  youtubeTvOnPc,
  zight,
  fasterweb,
  giphyForGmail,
  highlightThis,
  saveStoryInstagram,
  screenShader,
  skipAdsYoutube,
  adSkipperForYoutube,
  advancedAudioEqualizer,
  appblock,
  audioEqualizer,
  battleTabs,
  bestCursors,
  breathe,
  broMon,
  cheaperthere,
  crankwheel,
  cursorful,
  enhancedImageViewer,
  feedlyNotifier,
  fontstorage,
  goodMorningCloudhq,
  googleMapsExtendedRoutes,
  grabit,
  groundNews,
  htmlEditorForGmail,
  imgurUploader,
  inoreader,
  intouchapp,
  kommodo,
  mailMergeMailmeteor,
  mouseCursor,
  ntlModSlitherIo,
  postureMinder,
  previews,
  promptCard,
  ratepunk,
  remindersForGoogleCalendar,
  removeBackground,
  salesSortEbay,
  sauceForStrava,
  socialFocus,
  stormTracker,
  streamingEnhanced,
  syncWatch,
  synq,
  testingToolkit,
  theRssAggregator,
  themesongYoutubeMusic,
  tripadvisor,
  tzofar,
  ultimate432HzConverter,
  ultrawideStreaming,
  videoMaximizer,
  vkNext,
  wordReplacerMax,
};
